import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import i18next from 'i18next';
import { getAuthenticatedClient, getCurrentUserId } from '@/services/client';
import { edgeFunctions } from '@/services/edgeFunctions';
import { getSignedStorageUrl } from '@/features/audio/signedAudio';
import { prepareOriginalUpload, extensionFor, MAX_PHOTO_BYTES, toMb, type PrepareRefusal } from '@/features/photo/originalUpload';
import { APP_BUILD } from '@/lib/appBuild';
import { toast } from '@/hooks/use-toast';
import type { Database } from '@/integrations/supabase/types';

type PhotoCategory = Database['public']['Enums']['photo_category'];

export interface Photo {
  id: string;
  user_id: string;
  activity_id: string | null;
  farm_id: string | null;
  farmer_cycle_id: string | null;
  storage_path: string;
  thumbnail_path: string | null;
  file_size_bytes: number | null;
  phash: string | null;
  photo_category: string | null;
  crop_id: string | null;
  stage_id: string | null;
  gps_lat: number | null;
  gps_lng: number | null;
  gps_accuracy: number | null;
  ai_analysis: unknown | null;
  analyzed_at: string | null;
  fraud_flags: unknown | null;
  is_fraud_reviewed: boolean;
  is_fraud_confirmed: boolean;
  points_earned: number | null;
  is_synced: boolean;
  synced_at: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  upload_status: string;
  upload_error: string | null;
  upload_completed_at: string | null;
  batch_id?: string | null;
}

/** What the B2 upload returns to the screens: the server's photo row plus the chain facts. */
export interface UploadedPhoto extends Photo {
  publicUrl: string;
  /** The one server mirror activity for this photo. */
  activity_id: string | null;
  /** 'started' when the server began the analysis, 'skipped' on a repeat of the same photo id. */
  analysis: 'started' | 'skipped';
  /** Always 0 for CBS today (no ledger award, see FIX-CBS-B2-SERVER-CHAIN). */
  points_awarded: number;
}

// Fetch all photos
export const usePhotos = (activityId?: string) => {
  return useQuery({
    queryKey: activityId ? ['photos', activityId] : ['photos'],
    queryFn: async () => {
      const client = getAuthenticatedClient();
      const userId = await getCurrentUserId();
      if (!userId) return [];

      let query = client
        .from('photos')
        .select('*')
        .eq('user_id', userId);

      if (activityId) {
        query = query.eq('activity_id', activityId);
      }

      const { data, error } = await query.order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as Photo[];
    },
    staleTime: 1000 * 60 * 5,
  });
};

const refusalMessage = (reason: PrepareRefusal, bytes: number): string => {
  switch (reason) {
    case 'too_large':
      return i18next.t('camera.fileTooLarge', { size: toMb(bytes), max: MAX_PHOTO_BYTES / (1024 * 1024) });
    case 'heic_decode_failed':
      return i18next.t('camera.heicConvertFailed');
    default:
      return i18next.t('camera.unsupportedPhotoType');
  }
};

// FIX-CBS-B2-SERVER-CHAIN (CCS Offline Stage B, unit B2): ONE call. The phone no longer writes to
// storage or to the photos / activities tables. upload-photo (the same edge function CCS uses) stores
// the original bytes and the preview, reads the EXIF, inserts the photo row, links the farmer's batch,
// creates the one mirror activity and starts the analysis. The phone sends app=cbs and its batch id.
// A1 phone half is kept: HEIC is converted on the phone, a ~320 px preview is made, and an over-limit
// or unusable file is refused with a translated message (never shrunk to fit).
export const useUploadPhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      photoCategory,
      batchId,
      gpsLat,
      gpsLng,
      gpsAccuracy,
      onProgress,
      precaptureMetadata,
    }: {
      file: File;
      /** Kept for call-site compatibility; the server links the photo to its own mirror activity. */
      activityId?: string;
      photoCategory?: PhotoCategory;
      batchId?: string;
      gpsLat?: number;
      gpsLng?: number;
      gpsAccuracy?: number;
      onProgress?: (progress: number) => void;
      precaptureMetadata?: Record<string, unknown>;
    }): Promise<UploadedPhoto> => {
      const userId = getCurrentUserId();
      if (!userId) throw new Error('Not authenticated');

      // A1: original bytes untouched; HEIC converted once; preview made here; refusals are translated.
      const prepared = await prepareOriginalUpload(file);
      if ('refused' in prepared) {
        throw new Error(refusalMessage(prepared.refused, file.size));
      }
      onProgress?.(25);

      const photoId = crypto.randomUUID();
      const loc = precaptureMetadata?.location as
        | { latitude?: number; longitude?: number; accuracy_m?: number }
        | undefined;
      const lat = gpsLat ?? loc?.latitude;
      const lng = gpsLng ?? loc?.longitude;
      const acc = gpsAccuracy ?? loc?.accuracy_m;

      const formData = new FormData();
      formData.append('file', prepared.original, `${photoId}.${extensionFor[prepared.detected]}`);
      if (prepared.thumbnail) formData.append('thumbnail', prepared.thumbnail, `${photoId}_thumb.jpg`);
      formData.append('upload_transform', prepared.transform);
      formData.append('upload_source', 'live');
      formData.append('client_chain', 'server');
      formData.append('app', 'cbs');
      if (batchId) formData.append('batchId', batchId);
      formData.append('photoId', photoId);
      if (photoCategory) formData.append('category', photoCategory);
      formData.append('fileSize', prepared.original.size.toString());
      // R18 build stamp rides inside precapture_metadata.app, whatever the phone gathered.
      formData.append('precapture_metadata', JSON.stringify({ ...(precaptureMetadata ?? {}), app: { ...APP_BUILD } }));
      if (typeof lat === 'number') formData.append('gpsLat', String(lat));
      if (typeof lng === 'number') formData.append('gpsLng', String(lng));
      if (typeof acc === 'number') formData.append('gpsAccuracy', String(acc));

      const reply = await edgeFunctions.uploadPhoto(formData);
      onProgress?.(90);
      if (!reply.ok || !reply.body?.success || !reply.body.photo) {
        throw new Error(reply.body?.error || `Upload failed (${reply.status})`);
      }
      const row = reply.body.photo as unknown as Photo;
      const storagePath = reply.body.storagePath || row.storage_path;

      // The photos bucket is private — sign the stored object for display (a read, as before).
      const signedUrl = await getSignedStorageUrl(storagePath, 'photos');
      onProgress?.(100);

      return {
        ...row,
        storage_path: storagePath,
        publicUrl: signedUrl ?? '',
        activity_id: reply.body.activity_id ?? row.activity_id ?? null,
        analysis: reply.body.analysis === 'started' ? 'started' : 'skipped',
        points_awarded: typeof reply.body.points_awarded === 'number' ? reply.body.points_awarded : 0,
      };
    },

    onSuccess: () => {
      toast({ title: i18next.t("camera.photoUploaded", { defaultValue: "Photo uploaded" }), description: i18next.t("camera.photoUploadedDesc", { defaultValue: "Your photo has been added successfully" }) });
      queryClient.invalidateQueries({ queryKey: ['photos'] });
      // Home-screen task ring reads today's biosec photos; any photo upload
      // that tagged a batch_id could tick the biosec tile, so refresh it.
      queryClient.invalidateQueries({ queryKey: ['cbs', 'today-task-status'] });
    },

    onError: (error) => {
      toast({
        title: i18next.t("camera.uploadFailed", { defaultValue: "Upload failed" }),
        description: error instanceof Error ? error.message : i18next.t("common.unknownError", { defaultValue: "Unknown error" }),
        variant: 'destructive',
      });
    },
  });
};

// Delete photo
export const useDeletePhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const client = getAuthenticatedClient();
      const { error } = await client.storage.from('photos').remove([id]);
      if (error) throw error;
    },

    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['photos'] });
      const previousPhotos = queryClient.getQueryData<Photo[]>(['photos']);
      queryClient.setQueryData<Photo[]>(['photos'], (old = []) =>
        old.filter((photo) => photo.id !== id)
      );
      return { previousPhotos };
    },

    onError: (error, _, context) => {
      if (context?.previousPhotos) {
        queryClient.setQueryData(['photos'], context.previousPhotos);
      }
      toast({
        title: i18next.t("camera.deleteFailed", { defaultValue: "Failed to delete photo" }),
        description: error instanceof Error ? error.message : i18next.t("common.unknownError", { defaultValue: "Unknown error" }),
        variant: 'destructive',
      });
    },

    onSuccess: () => {
      toast({ title: i18next.t("camera.photoDeleted", { defaultValue: "Photo deleted" }), description: i18next.t("camera.photoDeletedDesc", { defaultValue: "The photo has been removed" }) });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['photos'] });
    },
  });
};
